import https from '../init/https';
import { getAnonymousId } from '../utils/anonymousId';

/*
 * Price sort sent to the search APIs as ?sort=price_asc | price_desc
 * (nothing for "Relevant"). Accepts the page's values: 'low' / 'high',
 * 'Low to High' / 'High to Low' or the codes themselves.
 */
const SORT_CODES = {
    low: 'price_asc',
    'low to high': 'price_asc',
    price_asc: 'price_asc',
    high: 'price_desc',
    'high to low': 'price_desc',
    price_desc: 'price_desc'
};
export const toSortCode = (value) => SORT_CODES[String(value || '').trim().toLowerCase()] || '';

//USER-SER-01 : Trip search
export async function tripSearchFun(
    destination,
    maxDays,
    category,
    baseCity,
    dates,
    group,
    minPrice,
    maxPrice,
    difficulty,
    maxNights,
    offset,
    limit,
    sortBy,
    saveRecent = false
) {
    const queryParams = new URLSearchParams();

    /*
     * =========================
     * DESTINATION
     * =========================
     */
    if (destination) {
        queryParams.append('destination', destination);
    }

    /*
     * =========================
     * DAYS
     * =========================
     */
    if (maxDays) {
        queryParams.append('max_days', maxDays);
    }

    /*
     * =========================
     * TRIP TYPE
     * =========================
     *
     * Adventure
     * Trek
     * etc.
     */
    if (category) {
        queryParams.append('type', category);
    }

    /*
     * =========================
     * BASE CITY
     * =========================
     */
    if (baseCity && !baseCity.includes('All')) {
        queryParams.append('base_city', baseCity);
    }

    /*
     * =========================
     * DATES
     * =========================
     */
    if (dates) {
        queryParams.append('dates', dates);
    }

    /*
     * =========================
     * GROUP
     * =========================
     */
    if (group) {
        queryParams.append('group', group);
    }

    /*
     * =========================
     * PRICE
     * =========================
     */
    if (minPrice !== undefined && minPrice !== '') {
        queryParams.append('price_min', minPrice);
    }

    if (maxPrice !== undefined && maxPrice !== '') {
        queryParams.append('price_max', maxPrice);
    }

    /*
     * =========================
     * DIFFICULTY
     * =========================
     */
    if (difficulty) {
        queryParams.append('difficulty', difficulty);
    }

    /*
     * =========================
     * NIGHTS
     * =========================
     */
    if (maxNights) {
        queryParams.append('max_nights', maxNights);
    }

    /*
     * =========================
     * PAGINATION
     * =========================
     */
    if (offset !== undefined) {
        queryParams.append('offset', offset);
    }

    if (limit !== undefined) {
        queryParams.append('limit', limit);
    }

    /*
     * =========================
     * SORT
     * =========================
     */
    if (toSortCode(sortBy)) {
        queryParams.append('sort', toSortCode(sortBy));
    }

    // Remember this search as a "recent search" (first page only)
    if (saveRecent && destination) {
        queryParams.append('saveRecent', 'true');
        queryParams.append('anonymousId', getAnonymousId());
    }

    const queryString = queryParams.toString();

    const result = await https.get(`/search/trip?${queryString}`);

    return result;
}

//USER-SER-02 : Ayurveda search
export async function ayurSearchFun(text, offset, limit) {
    const result = await https.get(`/search/ayur?value=${text}&offset=${offset}&limit=${limit}`);
    return result;
}

//USER-SER-03 : Guid search
export async function guSearchFun(
    text,
    base_city,
    area,
    languages,
    value,
    minPrice,
    maxPrice,
    offset,
    limit,
    sortBy,
    saveRecent = false
) {
    const queryParams = new URLSearchParams();
    if (base_city) queryParams.append('base_city', '');
    if (area) queryParams.append('area', area);
    if (minPrice !== undefined) queryParams.append('price_min', minPrice);
    if (maxPrice !== undefined) queryParams.append('price_max', maxPrice);
    if (text) queryParams.append('place', text);
    if (languages) queryParams.append('languages', languages);
    if (offset !== undefined) queryParams.append('offset', offset);
    if (limit !== undefined) queryParams.append('limit', limit);
    if (toSortCode(sortBy)) queryParams.append('sort', toSortCode(sortBy));
    if (saveRecent && text) {
        queryParams.append('saveRecent', 'true');
        queryParams.append('anonymousId', getAnonymousId());
    }
    const queryString = queryParams.toString();
    const result = await https.get(`/search/gu?${queryString}`);
    return result;
}
// USER-SER-04 : Camp / Accommodation search (GET with query params)
export async function campSearchFun({
    place = '',
    checkin = '',
    checkout = '',
    adults = 1,
    children = 0,
    pets = 0,
    propertyType = '',
    propertyCategory = '',
    // sidebar filters (comma separated lists)
    propertyTypes = '',
    unitTypes = '',
    amenities = '',
    price = '',
    minPrice = '',
    maxPrice = '',
    offset = 0,
    limit = 9,
    sortBy = '',
    saveRecent = false
}) {
    const params = {
        place,
        checkIn: checkin,
        checkOut: checkout,
        adults,
        children,
        pets,
        propertyType,
        propertyCategory,
        propertyTypes,
        unitTypes,
        amenities,
        maxPrice: maxPrice || price,
        minPrice,
        sort: toSortCode(sortBy),
        anonymousId: getAnonymousId(),
        offset,
        limit,
        saveRecent: saveRecent ? 'true' : ''
    };

    // Don't send empty values (an empty price would mean "max ₹0")
    Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
    });

    const result = await https.get('/search/camp', { params });

    return result;
}

// USER-SER-07 : Activity search
export async function actSearchFun({
    place = '',
    activity = '',
    minPrice = '',
    maxPrice = '',
    offset = 0,
    limit = 9,
    sortBy = '',
    saveRecent = false
}) {
    const queryParams = new URLSearchParams();

    queryParams.append('place', place);
    if (toSortCode(sortBy)) queryParams.append('sort', toSortCode(sortBy));
    queryParams.append('activity', activity);
    queryParams.append('price_min', minPrice);
    queryParams.append('price_max', maxPrice);
    queryParams.append('offset', offset);
    queryParams.append('limit', limit);

    if (saveRecent && (place || activity)) {
        queryParams.append('saveRecent', 'true');
        queryParams.append('anonymousId', getAnonymousId());
    }

    return https.get(`/search/act?${queryParams.toString()}`);
}

//USER-SER-05 : Blog search
export async function blogSearchFun(text, offset, limit) {
    const result = await https.get(`/search/blog?value=${text}&offset=${offset}&limit=${limit}`);
    return result;
}

// USER-SER-06 : Search autocomplete
//
// Suggestions are kept in memory for a few minutes, so typing the same
// letters again (or going back and forth) shows results instantly with no
// network call. withRecent=true also returns the user's recent searches:
// response becomes { suggestions, recentSearches } and is never cached.
const AUTOCOMPLETE_TTL_MS = 3 * 60 * 1000;
const autocompleteCache = new Map();

export async function searchAutocomplete({ type, value = '', place = '', activity, limit, catlim, withRecent = false }) {
    const params = {
        value,
        place,
        activity,
        limit,
        catlim
    };
    params.anonymousId = getAnonymousId();
    if (withRecent) params.withRecent = 'true';

    const cacheKey = JSON.stringify([type, value.trim().toLowerCase(), place, activity, limit, catlim]);

    // Camp's empty-box response already includes the user's recent searches
    const cacheable = !withRecent && !(type === 'camp' && !value.trim());

    if (cacheable) {
        const hit = autocompleteCache.get(cacheKey);
        if (hit && hit.expiresAt > Date.now()) return hit.response;
    }

    const response = await https.get(`/search/auto/${type}`, {
        params
    });

    if (cacheable) {
        autocompleteCache.set(cacheKey, { response, expiresAt: Date.now() + AUTOCOMPLETE_TTL_MS });
        if (autocompleteCache.size > 200) autocompleteCache.delete(autocompleteCache.keys().next().value);
    }

    return response;
}
